import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  getIncidents,
  updateIncident,
  getBarangays,
  getUsers,
} from '../../lib/api';
import {
  Ambulance,
  Clock,
  CheckCircle,
  AlertTriangle,
  MapPin,
  Phone,
  User,
  Image,
  RefreshCw,
} from 'lucide-react';

export default function IncidentManagement() {
  const { user, isMunicipalAdmin, isEMT } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [emtUsers, setEmtUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBarangay, setFilterBarangay] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [incRes, brgyRes, usersRes] = await Promise.all([
        getIncidents(),
        getBarangays(),
        getUsers({ role: 'emt' }).catch(() => ({ data: [] })),
      ]);
      setIncidents(incRes.data);
      setBarangays(brgyRes.data);
      setEmtUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (status, additionalData = {}) => {
    if (!selectedIncident) return;
    setUpdateLoading(true);
    
    try {
      const updateData = { status, ...additionalData };
      
      if (status === 'acknowledged') {
        updateData.time_acknowledged = new Date().toISOString();
      } else if (status === 'dispatched') {
        updateData.time_dispatched = new Date().toISOString();
      } else if (status === 'arrived') {
        updateData.time_arrived = new Date().toISOString();
      }
      
      await updateIncident(selectedIncident.id, updateData);
      toast.success(`Incident ${status}`);
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update incident');
    } finally {
      setUpdateLoading(false);
    }
  };

  const openIncidentDetails = (incident) => {
    setSelectedIncident(incident);
    setDialogOpen(true);
  };

  const filteredIncidents = incidents.filter(incident => {
    const matchesStatus = filterStatus === 'all' || incident.status === filterStatus;
    const matchesBarangay = filterBarangay === 'all' || incident.barangay_id === filterBarangay;
    return matchesStatus && matchesBarangay;
  });

  const getBarangayName = (id) => barangays.find(b => b.id === id)?.name || 'Unknown';
  const getEMTName = (id) => emtUsers.find(u => u.id === id)?.full_name || 'Unassigned';

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'acknowledged': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'dispatched': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'arrived': return 'bg-teal-100 text-teal-800 border-teal-300';
      case 'completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled': return 'bg-slate-100 text-slate-800 border-slate-300';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Medical': return 'default';
      case 'Fire': return 'destructive';
      case 'Police': return 'secondary';
      default: return 'outline';
    }
  };

  const stats = {
    pending: incidents.filter(i => i.status === 'pending').length,
    active: incidents.filter(i => ['acknowledged', 'dispatched'].includes(i.status)).length,
    escalated: incidents.filter(i => i.escalated).length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="incident-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              Emergency Dispatch
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage and track emergency incidents
            </p>
          </div>
          <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={stats.pending > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className={`h-8 w-8 ${stats.pending > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.active > 0 ? 'border-blue-300 bg-blue-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Ambulance className={`h-8 w-8 ${stats.active > 0 ? 'text-blue-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.active}</p>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.escalated > 0 ? 'border-red-300 bg-red-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`h-8 w-8 ${stats.escalated > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.escalated}</p>
                  <p className="text-sm text-muted-foreground">Escalated</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-status">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="arrived">Arrived</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              {isMunicipalAdmin() && (
                <Select value={filterBarangay} onValueChange={setFilterBarangay}>
                  <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-barangay">
                    <SelectValue placeholder="Filter by barangay" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Barangays</SelectItem>
                    {barangays.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ambulance className="h-5 w-5 text-primary" />
              Incidents
            </CardTitle>
            <CardDescription>
              {filteredIncidents.length} incidents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Location</TableHead>
                    {isMunicipalAdmin() && <TableHead>Barangay</TableHead>}
                    <TableHead>EMT</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredIncidents.map((incident) => (
                    <TableRow 
                      key={incident.id} 
                      data-testid={`incident-row-${incident.id}`}
                      className={incident.escalated ? 'bg-red-50/50' : ''}
                    >
                      <TableCell className="text-sm">
                        {new Date(incident.time_received).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getTypeColor(incident.incident_type)}>
                          {incident.incident_type}
                        </Badge>
                        {incident.escalated && (
                          <Badge variant="destructive" className="ml-1 text-xs">
                            ESCALATED
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium border ${getStatusColor(incident.status)}`}>
                          {incident.status}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm">
                        {incident.location_text || 'Not specified'}
                      </TableCell>
                      {isMunicipalAdmin() && (
                        <TableCell className="text-sm">{getBarangayName(incident.barangay_id)}</TableCell>
                      )}
                      <TableCell className="text-sm">
                        {getEMTName(incident.assigned_emt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openIncidentDetails(incident)}
                          data-testid={`view-incident-${incident.id}`}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredIncidents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isMunicipalAdmin() ? 7 : 6} className="text-center py-8 text-muted-foreground">
                        No incidents found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Incident Details Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Badge variant={getTypeColor(selectedIncident?.incident_type)}>
                  {selectedIncident?.incident_type}
                </Badge>
                Emergency #{selectedIncident?.id?.slice(0, 8)}
              </DialogTitle>
              <DialogDescription>
                Received: {selectedIncident && new Date(selectedIncident.time_received).toLocaleString()}
              </DialogDescription>
            </DialogHeader>

            {selectedIncident && (
              <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                  <span className="text-sm font-medium">Current Status</span>
                  <span className={`px-3 py-1 rounded-sm text-sm font-medium border ${getStatusColor(selectedIncident.status)}`}>
                    {selectedIncident.status}
                  </span>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <MapPin className="h-4 w-4" />
                    Location
                  </div>
                  <p className="text-sm text-muted-foreground pl-6">
                    {selectedIncident.location_text || 'Not specified'}
                  </p>
                  {selectedIncident.location_lat && (
                    <p className="text-xs text-muted-foreground pl-6">
                      GPS: {selectedIncident.location_lat}, {selectedIncident.location_lng}
                    </p>
                  )}
                </div>

                {/* Contact */}
                {selectedIncident.mobile_verified && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Phone className="h-4 w-4" />
                      Contact
                    </div>
                    <p className="text-sm text-muted-foreground pl-6">
                      {selectedIncident.mobile_verified}
                    </p>
                  </div>
                )}

                {/* Selfie */}
                {selectedIncident.selfie_data && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Image className="h-4 w-4" />
                      Verification Photo
                    </div>
                    <img
                      src={selectedIncident.selfie_data}
                      alt="Verification"
                      className="w-full max-w-[200px] rounded-sm border"
                    />
                  </div>
                )}

                {/* Timeline */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Clock className="h-4 w-4" />
                    Timeline
                  </div>
                  <div className="pl-6 space-y-1 text-sm">
                    <p>Received: {new Date(selectedIncident.time_received).toLocaleString()}</p>
                    {selectedIncident.time_acknowledged && (
                      <p>Acknowledged: {new Date(selectedIncident.time_acknowledged).toLocaleString()}</p>
                    )}
                    {selectedIncident.time_dispatched && (
                      <p>Dispatched: {new Date(selectedIncident.time_dispatched).toLocaleString()}</p>
                    )}
                    {selectedIncident.time_arrived && (
                      <p>Arrived: {new Date(selectedIncident.time_arrived).toLocaleString()}</p>
                    )}
                  </div>
                </div>

                {/* Assigned EMT */}
                {selectedIncident.assigned_emt && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      Assigned EMT
                    </div>
                    <p className="text-sm text-muted-foreground pl-6">
                      {getEMTName(selectedIncident.assigned_emt)}
                    </p>
                  </div>
                )}

                {/* Actions */}
                {selectedIncident.status !== 'completed' && selectedIncident.status !== 'cancelled' && (
                  <div className="pt-4 border-t space-y-3">
                    <p className="text-sm font-medium">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedIncident.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus('acknowledged')}
                          disabled={updateLoading}
                          data-testid="acknowledge-btn"
                        >
                          Acknowledge
                        </Button>
                      )}
                      {selectedIncident.status === 'acknowledged' && (
                        <>
                          <Select onValueChange={(emt) => handleUpdateStatus('dispatched', { assigned_emt: emt })}>
                            <SelectTrigger className="w-[200px]">
                              <SelectValue placeholder="Dispatch to EMT..." />
                            </SelectTrigger>
                            <SelectContent>
                              {emtUsers.map((emt) => (
                                <SelectItem key={emt.id} value={emt.id}>{emt.full_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </>
                      )}
                      {selectedIncident.status === 'dispatched' && (
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus('arrived')}
                          disabled={updateLoading}
                          data-testid="arrived-btn"
                        >
                          Mark Arrived
                        </Button>
                      )}
                      {selectedIncident.status === 'arrived' && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleUpdateStatus('completed', { outcome: 'Resolved' })}
                          disabled={updateLoading}
                          data-testid="complete-btn"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Complete
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
